import { SyntheticEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useKeycloak } from '@react-keycloak/web';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Autocomplete, TextField as MuiTextField } from '@mui/material';
import { Moment } from 'moment';
import { setErrorFeedback, setSuccessFeedback } from '../../../../features/feedback/feedbackSlice';
import {
  selectUnidadesDeNegocioAssociadas,
  selectUnidadeSelecionada,
} from '../../../../features/usuario-logado/usuarioLogadoSlice';
import { GridItemModalProps } from '../../../common/components/cardsGrid/cardsModalGrid';
import SelectStyled from '../../../common/components/input/selectStyled';
import { useListClientes } from '../../../common/hooks/useListClientes';
import QCXDatePicker from '../../../../shared-components/date-picker/QCXDatePicker';
import splitPartnumbers from '../../../common/utils/splitPartnumbers';
import { generateSpreadsheetItensDi } from './relatorioItensDiPage.helpers';

interface ControlledValue {
  id: string | number;
  value: string;
}

interface IUnidadeDeNegocio {
  id: number;
  pessoa: { nome: string; nomeResumido: string };
}

const useStyles = makeStyles({
  root: {
    '& .MuiDialog-paper': {
      minWidth: '52vw',
    },
  },
});

const RelatorioItensDiPage = ({ title, isOpen, closeModal }: GridItemModalProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { keycloak } = useKeycloak();
  const { token } = keycloak;

  const selectedUnit = useSelector(selectUnidadeSelecionada);
  const businessUnits: IUnidadeDeNegocio[] = useSelector(selectUnidadesDeNegocioAssociadas);
  const [clientes] = useListClientes();

  const [selectedBusiness, setSelectedBusiness] = useState<ControlledValue | undefined>(undefined);
  const [selectedCliente, setSelectedCliente] = useState<ControlledValue | undefined>(undefined);
  const [processos, setProcessos] = useState<string[]>([]);
  const [dataInicio, setDataInicio] = useState<Moment | null>(null);
  const [dataFim, setDataFim] = useState<Moment | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedBusiness || !selectedUnit) return;

    const unidadePrincipal = businessUnits.find((bu) => Number(bu.id) === Number(selectedUnit));

    if (unidadePrincipal) {
      setSelectedBusiness({
        id: unidadePrincipal.id,
        value: unidadePrincipal.pessoa.nomeResumido ?? unidadePrincipal.pessoa.nome,
      });
    }
  }, [businessUnits, selectedUnit, selectedBusiness]);

  const tenantId = selectedBusiness ? Number(selectedBusiness.id) : selectedUnit;

  const handleBusinessChange = (
    _event: SyntheticEvent,
    value: { id: string | number | undefined; value: string | undefined } | null
  ) => {
    if (value == null) setSelectedBusiness(undefined);
    else setSelectedBusiness({ id: value.id as string, value: value.value as string });
  };

  const handleClienteChange = (
    _event: SyntheticEvent,
    value: { id: string | number | undefined; value: string | undefined } | null
  ) => {
    if (value == null) setSelectedCliente(undefined);
    else setSelectedCliente({ id: value.id as string, value: value.value as string });
  };

  const handleSubmit = async () => {
    if (!token || !tenantId) {
      dispatch(setErrorFeedback({ message: 'Selecione uma unidade de negócio.' }));
      return;
    }

    if (!dataInicio || !dataFim) {
      dispatch(setErrorFeedback({ message: 'Informe o período de data de registro.' }));
      return;
    }

    if (dataInicio.isAfter(dataFim)) {
      dispatch(setErrorFeedback({ message: 'A data de início não pode ser posterior à data de fim.' }));
      return;
    }

    setLoading(true);
    keycloak.updateToken(300);

    try {
      const error = await generateSpreadsheetItensDi(
        token,
        tenantId,
        dataInicio.format('YYYY-MM-DD'),
        dataFim.format('YYYY-MM-DD'),
        selectedCliente ? String(selectedCliente.id) : undefined,
        processos.length > 0 ? processos : undefined
      );

      if (error) {
        dispatch(setErrorFeedback({ message: error }));
      } else {
        dispatch(setSuccessFeedback({ message: 'Relatório gerado com sucesso!' }));
        closeModal();
      }
    } catch {
      dispatch(setErrorFeedback({ message: 'Erro inesperado ao gerar o relatório.' }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog className={classes.root} open={isOpen} onClose={closeModal}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <QCXDatePicker label="Data de Registro (Início)" value={dataInicio} onChange={setDataInicio} />
          </Grid>

          <Grid item xs={6}>
            <QCXDatePicker label="Data de Registro (Fim)" value={dataFim} onChange={setDataFim} />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <SelectStyled
                disablePortal={false}
                controlledValue={selectedBusiness}
                options={businessUnits.map((bu) => ({
                  id: bu.id,
                  value: bu.pessoa.nomeResumido ?? bu.pessoa.nome,
                }))}
                onChangeAction={handleBusinessChange}
                label="Unidade de Negócio"
              />
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <SelectStyled
                disablePortal={false}
                required={false}
                controlledValue={selectedCliente}
                options={clientes.map((c) => ({
                  id: c.id ?? '',
                  value: `(${c.pessoa?.cnpj ?? ''}) ${c.pessoa?.nome ?? ''}`,
                }))}
                onChangeAction={handleClienteChange}
                label="Cliente"
              />
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={[] as string[]}
              value={processos}
              autoSelect
              onChange={(_event, newValue) => setProcessos(splitPartnumbers(newValue as string[]))}
              renderInput={(params) => (
                <MuiTextField
                  {...params}
                  label="PROCESSO"
                  placeholder="Digite e pressione Enter"
                  InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Box padding={2} display="flex" gridGap={8}>
          <Button variant="outlined" color="primary" onClick={closeModal} disabled={loading}>
            Fechar
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSubmit}
            disabled={loading || !tenantId || !dataInicio || !dataFim}
          >
            {loading ? 'Gerando...' : 'Gerar Relatório'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default RelatorioItensDiPage;
